import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { readAxiosDetail } from '../api_calls';
import {
  fetchCatalog,
  fetchSummary,
  createCatalogEntry,
  updateCatalogEntry,
  deleteCatalogEntry,
} from './analyticsApi';
import type { EventCatalogOut, EventCatalogCreate, EventCatalogUpdate, SummaryRow } from './analyticsTypes';
import CatalogFormDialog from './CatalogFormDialog';
import EmptyState from './components/EmptyState';
import { useAnalyticsFilters } from './hooks/useAnalyticsFilters';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../../utils/iconFallbacks';
const CATALOG_PAGE_SIZE = 25;

interface DriftSummary {
  total: number;
  /** Catalog entries with no events seen in the current window. */
  unseen: Set<string>;
  /** Counts keyed by event_name from summary. */
  countByName: Map<string, number>;
  /** Every catalog event name (paginated catalog fetched as a flat list). */
  allCatalogNames: Set<string>;
  /** Events seen in the wild but not in the catalog (uncatalogued). */
  uncatalogued: Array<{ event_name: string; count: number }>;
}

function isLegacyName(name: string): boolean {
  return name.startsWith('legacy.');
}

const CATALOG_FETCH_ALL_SIZE = 500; // backend caps catalog page_size at 500

export default function EventCatalog() {
  const { filters, setFilters } = useAnalyticsFilters();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [entries, setEntries] = useState<EventCatalogOut[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [drift, setDrift] = useState<DriftSummary | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<EventCatalogOut | null>(null);
  const [seedEventName, setSeedEventName] = useState<string | undefined>(undefined);
  const [deleteTarget, setDeleteTarget] = useState<EventCatalogOut | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadCatalog = useCallback(async (pageNum: number) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchCatalog({ page: pageNum + 1, page_size: CATALOG_PAGE_SIZE });
      setEntries(res.items);
      setTotal(res.total);
    } catch (e) {
      setError(readAxiosDetail(e));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDrift = useCallback(async () => {
    try {
      const [summary, allCatalog] = await Promise.all([
        fetchSummary({ since: filters.since, until: filters.until }),
        fetchCatalog({ page: 1, page_size: CATALOG_FETCH_ALL_SIZE }),
      ]);
      const countByName = new Map<string, number>();
      summary.rows.forEach((r: SummaryRow) => countByName.set(r.event_name, (countByName.get(r.event_name) ?? 0) + r.count));
      const allCatalogNames = new Set<string>(allCatalog.items.map((c) => c.event_name));
      const uncatalogued: Array<{ event_name: string; count: number }> = [];
      countByName.forEach((count, name) => {
        if (!allCatalogNames.has(name) && !isLegacyName(name) && count > 0) {
          uncatalogued.push({ event_name: name, count });
        }
      });
      uncatalogued.sort((a, b) => b.count - a.count);
      setDrift((prev) => ({
        total: prev?.total ?? 0,
        countByName,
        unseen: prev?.unseen ?? new Set(),
        allCatalogNames,
        uncatalogued,
      }));
    } catch {
      // Drift is best-effort.
    }
  }, [filters.since, filters.until]);

  useEffect(() => {
    void loadCatalog(page);
  }, [loadCatalog, page]);

  useEffect(() => {
    void loadDrift();
  }, [loadDrift]);

  // Recompute "unseen" once both lists are available.
  useEffect(() => {
    if (!drift) return;
    const unseen = new Set<string>();
    for (const e of entries) {
      const c = drift.countByName.get(e.event_name) ?? 0;
      if (c === 0) unseen.add(e.event_name);
    }
    if (unseen.size !== drift.unseen.size) {
      setDrift({ ...drift, unseen, total: entries.length });
    }
    // Note: uncatalogued list is independent of pagination — computed once per loadDrift.
  }, [drift, entries]);

  const handleCreate = (eventName?: string) => {
    setEditingEntry(null);
    setSeedEventName(eventName);
    setDialogOpen(true);
  };

  const handleEdit = (entry: EventCatalogOut) => {
    setEditingEntry(entry);
    setSeedEventName(undefined);
    setDialogOpen(true);
  };

  const handleSave = async (payload: EventCatalogCreate | EventCatalogUpdate) => {
    try {
      if (editingEntry) {
        await updateCatalogEntry(editingEntry.event_name, payload as EventCatalogUpdate);
      } else {
        await createCatalogEntry(payload as EventCatalogCreate);
      }
      await loadCatalog(page);
    } catch (e) {
      setError(readAxiosDetail(e));
      throw e;
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteCatalogEntry(deleteTarget.event_name);
      setDeleteTarget(null);
      await loadCatalog(page);
    } catch (e) {
      setError(readAxiosDetail(e));
    } finally {
      setDeleting(false);
    }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return entries;
    const q = search.toLowerCase();
    return entries.filter(
      (e) =>
        e.event_name.toLowerCase().includes(q) ||
        (e.category ?? '').toLowerCase().includes(q) ||
        (e.notes ?? '').toLowerCase().includes(q),
    );
  }, [entries, search]);

  const driftCount = drift?.unseen.size ?? 0;
  const uncataloguedCount = drift?.uncatalogued.length ?? 0;

  return (
    <Stack spacing={2}>
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {uncataloguedCount > 0 && (
        <Accordion
          variant="outlined"
          sx={{
            backgroundColor: (t) => t.palette.warning.light + '14',
            '&:before': { display: 'none' },
          }}
        >
          <AccordionSummary expandIcon={<Icon size={20} name="chevron-down" />}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Icon d={ICON_FALLBACKS.NewReleases} size={20} />
              <Typography variant="subtitle2" fontWeight={700}>
                {uncataloguedCount} uncatalogued event{uncataloguedCount === 1 ? '' : 's'} seen
              </Typography>
              <Typography variant="caption" color="text.secondary">
                — fired but not in the catalog
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Stack spacing={0.5} divider={<Box sx={{ borderBottom: 1, borderColor: 'divider' }} />}>
              {drift!.uncatalogued.slice(0, 20).map((u) => (
                <Stack
                  key={u.event_name}
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ py: 0.75 }}
                >
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                    {u.event_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums', minWidth: 48, textAlign: 'right' }}>
                    {u.count.toLocaleString()}
                  </Typography>
                  <Tooltip title="View events of this type" arrow>
                    <IconButton
                      size="small"
                      onClick={() => setFilters({ eventName: u.event_name, view: 'events' })}
                    >
                      <Icon name="arrow-up-right-from-square" size={20} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Create catalog entry for this event" arrow>
                    <IconButton
                      size="small"
                      onClick={() => handleCreate(u.event_name)}
                    >
                      <Icon name="plus" size={20} />
                    </IconButton>
                  </Tooltip>
                </Stack>
              ))}
              {drift!.uncatalogued.length > 20 && (
                <Typography variant="caption" color="text.secondary" sx={{ pt: 0.5 }}>
                  +{drift!.uncatalogued.length - 20} more not shown
                </Typography>
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
        <TextField
          size="small"
          placeholder="Search by name, category, or notes"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Icon name="magnifying-glass" size={20} />
                </InputAdornment>
              ),
            },
          }}
          sx={{ flex: 1, maxWidth: { xs: '100%', sm: 360 } }}
        />
        {driftCount > 0 && (
          <Tooltip
            title={`${driftCount} catalog ${driftCount === 1 ? 'entry has' : 'entries have'} no events in the selected window. Widen the date range to confirm.`}
            arrow
          >
            <Chip
              icon={<Icon name="triangle-exclamation" size={20} />}
              label={`${driftCount} unseen in window`}
              color="warning"
              size="small"
              variant="outlined"
            />
          </Tooltip>
        )}
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" onClick={() => handleCreate()} size="small">
          New entry
        </Button>
      </Stack>

      {loading && entries.length === 0 ? (
        <Stack spacing={1}>
          {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} variant="rectangular" height={36} />)}
        </Stack>
      ) : (
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Event name</TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Category</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Screen</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Trigger</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Type</TableCell>
                <TableCell align="right">Count</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((entry) => {
                const count = drift?.countByName.get(entry.event_name) ?? 0;
                const unseen = drift?.unseen.has(entry.event_name);
                return (
                  <TableRow key={entry.catalog_id} hover>
                    <TableCell>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-word' }}>
                          {entry.event_name}
                        </Typography>
                        {unseen && (
                          <Tooltip title="No events recorded in the current window" arrow>
                            <Icon name="triangle-exclamation" size={20} />
                          </Tooltip>
                        )}
                      </Stack>
                      {/* xs-only fold: category chip under name */}
                      {entry.category && (
                        <Box sx={{ display: { xs: 'block', sm: 'none' }, mt: 0.5 }}>
                          <Chip label={entry.category} size="small" />
                        </Box>
                      )}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      {entry.category ? <Chip label={entry.category} size="small" /> : '—'}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{entry.screen ?? '—'}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{entry.trigger ?? '—'}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{entry.instrumentation_type ?? '—'}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {count.toLocaleString()}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="View events of this type" arrow>
                        <IconButton
                          size="small"
                          onClick={() => setFilters({ eventName: entry.event_name, view: 'events' })}
                        >
                          <Icon name="arrow-up-right-from-square" size={20} />
                        </IconButton>
                      </Tooltip>
                      <IconButton size="small" onClick={() => handleEdit(entry)} aria-label={`Edit ${entry.event_name}`}>
                        <Icon name="pen-to-square" size={20} />
                      </IconButton>
                      <IconButton size="small" onClick={() => setDeleteTarget(entry)} aria-label={`Delete ${entry.event_name}`}>
                        <Icon name="trash" size={20} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <EmptyState
                      compact
                      title={search ? 'No matching entries' : 'No catalog entries'}
                      body={search ? 'Try a different search term.' : 'Create the first entry to begin.'}
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={CATALOG_PAGE_SIZE}
        rowsPerPageOptions={[CATALOG_PAGE_SIZE]}
        labelRowsPerPage={isMobile ? '' : 'Rows per page:'}
        sx={{
          '& .MuiTablePagination-toolbar': { flexWrap: 'wrap', justifyContent: 'center' },
          '& .MuiTablePagination-spacer': { display: { xs: 'none', sm: 'flex' } },
        }}
      />

      {dialogOpen && (
        <CatalogFormDialog
          open={dialogOpen}
          entry={editingEntry}
          initialEventName={seedEventName}
          onClose={() => {
            setDialogOpen(false);
            setSeedEventName(undefined);
          }}
          onSave={handleSave}
        />
      )}

      <Dialog open={deleteTarget !== null} onClose={() => setDeleteTarget(null)} fullWidth fullScreen={isMobile}>
        <DialogTitle>Delete catalog entry</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete the catalog entry for <strong>{deleteTarget?.event_name}</strong>? This will not
            delete the events themselves, only the catalog metadata.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
